/**
 * gateLinkUtils.js — Tor-/Tür-Integration in Strukturen (#611)
 *
 * Pure geometry and link helpers for snapping gates ('tor') onto linkable
 * parent structures (zaun, mauer, gebaeude, gewaechshaus) and keeping them
 * in sync when the parent moves, rotates or scales.
 *
 * Link data model (additive, lives in gate.data and is persisted via
 * toJSON(['data', ...])):
 *
 *   gate.data.link = {
 *     parentId: '<uuid>',  // stable data.objectId of the parent structure
 *     edgeIndex: 2,        // edge index (0 for lines and band walls, #697)
 *     t: 0.42,             // position 0–1 along that edge
 *   }
 *
 * The gate position is DERIVED from the link (never stored twice), so
 * parent transforms — including undo/redo — resync gates automatically.
 * Parents get a stable data.objectId (UUID) lazily: on first link and on
 * canvas load (migration for existing gardens).
 *
 * No fabric imports on purpose: everything works on duck-typed objects
 * exposing calcTransformMatrix()/calcLinePoints()/points/pathOffset, which
 * keeps this module unit-testable without a canvas.
 */

export const GATE_TYPE_ID = 'tor';
export const WINDOW_TYPE_ID = 'fenster';

/** All opening-element type ids that ride the same link/snap mechanic (#611, #690). */
export const OPENING_TYPE_IDS = [GATE_TYPE_ID, WINDOW_TYPE_ID];

/**
 * Allowed parent-structure types per opening kind (#690). A gate may sit on any
 * of the four structures; a window only makes sense in a building or greenhouse.
 */
export const OPENING_PARENT_TYPES = {
  [GATE_TYPE_ID]: ['zaun', 'mauer', 'gebaeude', 'gewaechshaus'],
  [WINDOW_TYPE_ID]: ['gebaeude', 'gewaechshaus'],
};

/**
 * Union of every type that can host ANY opening. Kept under the historical name
 * and shape so existing callers keep working unchanged; kind-specific filtering
 * goes through parentsForOpening() / findLinkableParents(canvas, allowedTypes).
 */
export const LINKABLE_TYPE_IDS = [...new Set(Object.values(OPENING_PARENT_TYPES).flat())];

/** Max distance (scene px = cm) at which a click/drag snaps onto an edge */
export const GATE_ATTACH_THRESHOLD = 20;
/** Distance (scene px = cm) a linked gate must be dragged away to detach */
export const GATE_DETACH_THRESHOLD = 40;
/** Visual depth of the gate rect across the structure (v1, refined in Block B) */
export const GATE_DEPTH_PX = 16;
/** Segments used to approximate ellipse/circle outlines for edge snapping */
export const ELLIPSE_SAMPLE_SEGMENTS = 48;

/** Gate height bounds from the TOR config (config/gardenObjectTypes.js) */
const GATE_HEIGHT_MIN_CM = 80;
const GATE_HEIGHT_MAX_CM = 250;

/** Height property key per linkable parent type */
const PARENT_HEIGHT_KEYS = {
  zaun: 'height',
  mauer: 'height',
  gebaeude: 'buildingHeight',
  gewaechshaus: 'ridgeHeight',
};

// ---------------------------------------------------------------------------
// Type guards & IDs
// ---------------------------------------------------------------------------

export const getGardenTypeId = (obj) =>
  obj?.data?.objectTypeId || obj?.data?.gardenObjectType || null;

export const isGate = (obj) => getGardenTypeId(obj) === GATE_TYPE_ID;

/** The opening type id ('tor' | 'fenster') of an object, or null. */
export const getOpeningTypeId = (obj) => {
  const typeId = getGardenTypeId(obj);
  return OPENING_TYPE_IDS.includes(typeId) ? typeId : null;
};

/** True for any opening element (gate OR window) — shares link/snap behaviour. */
export const isOpening = (obj) => OPENING_TYPE_IDS.includes(getGardenTypeId(obj));

/** Parent-structure types an opening of the given kind may attach to. */
export const parentsForOpening = (openingTypeId) =>
  OPENING_PARENT_TYPES[openingTypeId] || LINKABLE_TYPE_IDS;

export const isLinkableParent = (obj, allowedTypes = LINKABLE_TYPE_IDS) =>
  allowedTypes.includes(getGardenTypeId(obj));

/**
 * Generate a UUID for data.objectId. Falls back to a random string when
 * crypto.randomUUID is unavailable (older WebViews, some test environments).
 */
export const generateObjectId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `oid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Ensure an object carries a stable data.objectId (UUID). Assigns one lazily
 * if missing. Returns the id (or null for falsy input).
 */
export const ensureObjectId = (obj) => {
  if (!obj) return null;
  if (!obj.data) obj.data = {};
  if (!obj.data.objectId) {
    obj.data.objectId = generateObjectId();
  }
  return obj.data.objectId;
};

/**
 * Reset identity on cloned objects (paste/duplicate):
 * - linkable parents get a fresh objectId so links stay unambiguous
 * - openings (gates AND windows) lose their link (a pasted opening starts standalone)
 */
export const sanitizeClonedObjectData = (obj) => {
  if (!obj?.data) return;
  if (obj.data.objectId) {
    obj.data.objectId = generateObjectId();
  }
  if (isOpening(obj) && obj.data.link) {
    delete obj.data.link;
  }
};

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * Transform a center-relative local point to world (scene) coordinates using
 * the object's transform matrix (same semantics as fabric util.transformPoint).
 */
const transformLocalPoint = (obj, p) => {
  const m = obj.calcTransformMatrix();
  return {
    x: m[0] * p.x + m[2] * p.y + m[4],
    y: m[1] * p.x + m[3] * p.y + m[5],
  };
};

const segmentsFromPoints = (pts, closed) => {
  const edges = [];
  const count = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < count; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    edges.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
  }
  return edges;
};

const EPS = 1e-6;

/**
 * Min length-to-width ratio at which a four-cornered footprint counts as a
 * band rather than a block (#697).
 *
 * Must stay in lockstep with `centerlineFromFootprint()` in
 * scene3d/bandGeometry.js — 2D and 3D have to agree on what a band is, or a
 * gate sits on the centerline in the plan and next to the wall in 3D. A test
 * pins both to the same boundary.
 */
export const BAND_MIN_ASPECT = 3;

/** Types whose band-shaped footprints carry gates on the CENTERLINE (#697). */
const BAND_CENTERLINE_TYPE_IDS = ['mauer'];

/**
 * Volume types (a building/greenhouse footprint, not a band). Their outline
 * edges are the outer walls; without a capture band an opening only attaches
 * within the bare attach threshold of a wall LINE, so dragging one onto the
 * footprint interior fails to re-attach (#690). They get a capture band that
 * reaches across the footprint (whole interior for small buildings, capped for
 * large ones), so an opening dragged over the building snaps to the nearest wall.
 */
const VOLUME_CAPTURE_TYPE_IDS = ['gebaeude', 'gewaechshaus'];
/** Max distance (cm) an opening may sit from a wall and still capture it. */
export const VOLUME_EDGE_CAPTURE_MAX = 100;

/**
 * Derive the centerline of a band-shaped quadrilateral: it connects the
 * midpoints of the two SHORT edges (same construction as
 * `centerlineFromFootprint()` on the 3D side, here in canvas px instead of
 * world meters).
 *
 * @param {Array<{x,y}>} corners - four world-space corners, in order
 * @returns {{x1,y1,x2,y2,width}|null} null when not four corners or not band-shaped
 */
const centerlineFromCorners = (corners) => {
  if (corners.length !== 4) return null;

  const mid = (i, j) => ({
    x: (corners[i].x + corners[j].x) / 2,
    y: (corners[i].y + corners[j].y) / 2,
  });
  const edgeLen = (i) =>
    Math.hypot(corners[(i + 1) % 4].x - corners[i].x, corners[(i + 1) % 4].y - corners[i].y);

  // Edges 0/2 and 1/3 are opposite each other
  const spanA = (edgeLen(0) + edgeLen(2)) / 2;
  const spanB = (edgeLen(1) + edgeLen(3)) / 2;
  const width = Math.min(spanA, spanB);
  const length = Math.max(spanA, spanB);
  if (!(width > EPS) || length / width < BAND_MIN_ASPECT) return null;

  // Oriented like the FIRST long edge, so a gate keeps the angle (and with it
  // the hinge side of its door symbol, #611) it had on the outline edge.
  const [a, b] = spanA >= spanB ? [mid(3, 0), mid(1, 2)] : [mid(0, 1), mid(2, 3)];
  return { x1: a.x, y1: a.y, x2: b.x, y2: b.y, width };
};

/**
 * True when this parent's edges CAN collapse to a single band centerline.
 * Shape check only — whether the footprint is actually band-shaped is decided
 * by centerlineFromCorners(). Used for the edgeIndex migration fallback.
 */
const canYieldBandCenterline = (parent) =>
  BAND_CENTERLINE_TYPE_IDS.includes(getGardenTypeId(parent)) &&
  (parent?.type === 'rect' || parent?.type === 'polygon');

/**
 * Edges of a closed four-cornered footprint: the centerline for band-shaped
 * walls, the outline for everything else.
 *
 * A wall is normally drawn with the RECTANGLE tool (`MAUER.allowedTools`
 * starts with RECTANGLE). Its outline edges run along the OUTER faces of the
 * band — putting a gate's center there leaves it half a wall thickness beside
 * the wall (#697). Buildings and greenhouses keep their outline on purpose: a
 * door belongs in the outer wall. A squarish wall block is a volume, not a
 * band, and keeps its outline too.
 */
const edgesFromFootprint = (parent, corners) => {
  const typeId = getGardenTypeId(parent);
  if (BAND_CENTERLINE_TYPE_IDS.includes(typeId)) {
    const centerline = centerlineFromCorners(corners);
    if (centerline) {
      const { width, ...edge } = centerline;
      // The centerline sits half a wall thickness away from either face. With
      // the plain attach threshold a thick wall would be harder to hit than
      // before the #697 fix — so the band captures across its own width.
      return [{ ...edge, captureRadius: width / 2 }];
    }
  }

  const edges = segmentsFromPoints(corners, true);

  // Buildings/greenhouses are VOLUMES, not bands: their outline edges are the
  // outer walls. Without a capture band an opening only attaches within the
  // bare attach threshold of a wall LINE, so dragging one onto the footprint
  // interior neither re-attaches nor, once linked, feels like it belongs to the
  // building (#690). Give each outline edge a capture band that reaches across
  // the footprint — the whole interior for small buildings, capped for large
  // ones. Effect: drag an opening onto the building → it snaps to the nearest
  // wall; drag it off the building → it detaches. Only 4-corner footprints
  // (rect / rectangular polygon); odd polygons keep the bare outline.
  if (VOLUME_CAPTURE_TYPE_IDS.includes(typeId) && corners.length === 4) {
    const edgeLen = (i) =>
      Math.hypot(corners[(i + 1) % 4].x - corners[i].x, corners[(i + 1) % 4].y - corners[i].y);
    const spanA = (edgeLen(0) + edgeLen(2)) / 2;
    const spanB = (edgeLen(1) + edgeLen(3)) / 2;
    const captureRadius = Math.min(Math.min(spanA, spanB) / 2, VOLUME_EDGE_CAPTURE_MAX);
    return edges.map((edge) => ({ ...edge, captureRadius }));
  }

  return edges;
};

/**
 * Extract the world-space edge segments of a linkable parent structure.
 * Handles all shapes the four target types can be drawn with:
 * line (zaun/mauer), rect (mauer/gebaeude/gewaechshaus), polygon
 * (mauer/gebaeude/gewaechshaus), ellipse/circle (gebaeude).
 *
 * Exception: a band-shaped WALL yields its centerline as the single edge
 * instead of the four outline edges (#697) — see edgesFromFootprint().
 *
 * Scaling/rotation/translation are applied via calcTransformMatrix, so the
 * result is correct for transformed parents.
 *
 * @param {Object} parent - Fabric-like object
 * @returns {Array<{x1,y1,x2,y2}>} edges in world coordinates ([] if unsupported)
 */
export const getParentEdges = (parent) => {
  if (!parent) return [];

  switch (parent.type) {
    case 'line': {
      // calcLinePoints() returns endpoints relative to the object center
      const p = parent.calcLinePoints();
      const a = transformLocalPoint(parent, { x: p.x1, y: p.y1 });
      const b = transformLocalPoint(parent, { x: p.x2, y: p.y2 });
      return [{ x1: a.x, y1: a.y, x2: b.x, y2: b.y }];
    }

    case 'rect': {
      const w = parent.width || 0;
      const h = parent.height || 0;
      const corners = [
        { x: -w / 2, y: -h / 2 },
        { x: w / 2, y: -h / 2 },
        { x: w / 2, y: h / 2 },
        { x: -w / 2, y: h / 2 },
      ].map((c) => transformLocalPoint(parent, c));
      return edgesFromFootprint(parent, corners);
    }

    case 'polygon':
    case 'polyline': {
      const points = parent.points || [];
      if (points.length < 2) return [];
      const offX = parent.pathOffset?.x ?? 0;
      const offY = parent.pathOffset?.y ?? 0;
      // points are bbox-relative; calcTransformMatrix uses the center as
      // local origin — subtract pathOffset to convert (see vertexEditUtils)
      const world = points.map((p) =>
        transformLocalPoint(parent, { x: p.x - offX, y: p.y - offY })
      );
      if (parent.type === 'polygon') return edgesFromFootprint(parent, world);
      return segmentsFromPoints(world, false);
    }

    case 'ellipse':
    case 'circle': {
      const rx = parent.type === 'circle' ? parent.radius || 0 : parent.rx || 0;
      const ry = parent.type === 'circle' ? parent.radius || 0 : parent.ry || 0;
      if (rx <= 0 || ry <= 0) return [];
      const pts = [];
      for (let i = 0; i < ELLIPSE_SAMPLE_SEGMENTS; i++) {
        const theta = (i / ELLIPSE_SAMPLE_SEGMENTS) * Math.PI * 2;
        pts.push(
          transformLocalPoint(parent, {
            x: rx * Math.cos(theta),
            y: ry * Math.sin(theta),
          })
        );
      }
      return segmentsFromPoints(pts, true);
    }

    default:
      return [];
  }
};

/**
 * Closest point on segment (x1,y1)-(x2,y2) to point p.
 * @returns {{x, y, t, dist}} t is clamped to [0, 1]
 */
export const closestPointOnSegment = (p, x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;

  let t = 0;
  if (lenSq > 0) {
    t = ((p.x - x1) * dx + (p.y - y1) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
  }

  const x = x1 + t * dx;
  const y = y1 + t * dy;
  const distX = p.x - x;
  const distY = p.y - y;

  return { x, y, t, dist: Math.sqrt(distX * distX + distY * distY) };
};

const edgeAngleDeg = (edge) => {
  const angle = Math.atan2(edge.y2 - edge.y1, edge.x2 - edge.x1) * (180 / Math.PI);
  return angle < 0 ? angle + 360 : angle;
};

const edgeLength = (edge) => {
  const dx = edge.x2 - edge.x1;
  const dy = edge.y2 - edge.y1;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Interpolate the point at position t (0–1) along an edge.
 */
export const pointOnEdgeAt = (edge, t) => ({
  x: edge.x1 + t * (edge.x2 - edge.x1),
  y: edge.y1 + t * (edge.y2 - edge.y1),
});

const ORIGIN_FRACTIONS = { left: 0, top: 0, center: 0.5, right: 1, bottom: 1 };

const originFraction = (value) => {
  if (typeof value === 'number') return value;
  return ORIGIN_FRACTIONS[value] ?? 0.5;
};

/**
 * Position an object so its CENTER lands on the given world point, respecting
 * the object's own origin and rotation (origin-agnostic — legacy gates keep
 * their 'left'/'top' origin, newly placed gates use 'center').
 *
 * Set the target angle BEFORE calling this.
 */
export const setObjectCenterAt = (obj, center) => {
  const w = (obj.width || 0) * (obj.scaleX || 1);
  const h = (obj.height || 0) * (obj.scaleY || 1);
  const dx = (originFraction(obj.originX) - 0.5) * w;
  const dy = (originFraction(obj.originY) - 0.5) * h;
  const rad = ((obj.angle || 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  obj.set({
    left: center.x + dx * cos - dy * sin,
    top: center.y + dx * sin + dy * cos,
  });
};

/**
 * Find the nearest edge point across a set of linkable parents.
 *
 * @param {{x, y}} point - World-space point (click/drag position)
 * @param {Array<Object>} parents - Candidate parent objects
 * @param {number} threshold - Max snap distance in scene px
 * @returns {{parent, edgeIndex, edge, t, x, y, angleDeg, edgeLength, distance}|null}
 */
export const findNearestEdgePoint = (point, parents, threshold = GATE_ATTACH_THRESHOLD) => {
  let best = null;

  parents.forEach((parent) => {
    const edges = getParentEdges(parent);
    edges.forEach((edge, edgeIndex) => {
      const hit = closestPointOnSegment(point, edge.x1, edge.y1, edge.x2, edge.y2);
      // Band centerlines capture across the whole wall (see edgesFromFootprint).
      // Only candidacy widens — the winner is still the geometrically closest
      // edge, so a wide wall never outbids a nearer fence.
      const limit = Math.max(threshold, edge.captureRadius || 0);
      if (hit.dist <= limit && (!best || hit.dist < best.distance)) {
        best = {
          parent,
          edgeIndex,
          edge,
          t: hit.t,
          x: hit.x,
          y: hit.y,
          angleDeg: edgeAngleDeg(edge),
          edgeLength: edgeLength(edge),
          distance: hit.dist,
        };
      }
    });
  });

  return best;
};

/**
 * Clamp t so a gate of gateWidthPx stays fully on an edge of edgeLengthPx.
 * Falls back to the edge center when the gate is wider than the edge.
 */
export const clampGateT = (t, edgeLengthPx, gateWidthPx) => {
  if (!edgeLengthPx || edgeLengthPx <= 0) return 0.5;
  const halfRatio = gateWidthPx / 2 / edgeLengthPx;
  if (halfRatio >= 0.5) return 0.5;
  return Math.max(halfRatio, Math.min(1 - halfRatio, t));
};

/**
 * Derive the world position + angle for a gate link from the current parent
 * geometry. Returns null when the link cannot be resolved (missing edge).
 *
 * @param {Object} parent - Parent structure
 * @param {{edgeIndex, t}} link
 * @returns {{x, y, angleDeg, edgeLength}|null}
 */
export const getLinkWorldPosition = (parent, link) => {
  if (!parent || !link) return null;

  const edges = getParentEdges(parent);
  let edge = edges[link.edgeIndex];

  // Migration (#697): band walls used to expose four outline edges, so saved
  // gardens carry edgeIndex 0–3. They now expose only the centerline — without
  // this fallback the link would resolve to nothing and the gate would
  // SILENTLY stop following its wall (no error, it just stays put).
  if (!edge && edges.length === 1 && canYieldBandCenterline(parent)) {
    edge = edges[0];
  }
  if (!edge) return null;

  const t = Math.max(0, Math.min(1, link.t ?? 0.5));
  return {
    x: edge.x1 + t * (edge.x2 - edge.x1),
    y: edge.y1 + t * (edge.y2 - edge.y1),
    angleDeg: edgeAngleDeg(edge),
    edgeLength: edgeLength(edge),
    edge,
  };
};

// ---------------------------------------------------------------------------
// Height inheritance
// ---------------------------------------------------------------------------

/**
 * Read the parent's height in cm (property key differs per type).
 * @returns {number|null}
 */
export const getParentHeightCm = (parent) => {
  const typeId = getGardenTypeId(parent);
  const key = PARENT_HEIGHT_KEYS[typeId];
  const value = key ? parent?.data?.[key] : null;
  return typeof value === 'number' && !Number.isNaN(value) ? value : null;
};

/**
 * Apply the parent's height onto a gate with heightMode 'auto', clamped to
 * the TOR config bounds (80–250 cm). No-op for custom height mode.
 */
export const applyInheritedHeight = (gate, parent) => {
  if (!gate?.data || gate.data.heightMode !== 'auto') return;
  const parentHeight = getParentHeightCm(parent);
  if (parentHeight === null) return;
  gate.data.height = Math.max(
    GATE_HEIGHT_MIN_CM,
    Math.min(GATE_HEIGHT_MAX_CM, parentHeight)
  );
};

/**
 * Normalize a fabric Line's raw x1..y2 coordinates to its CURRENT world
 * endpoints.
 *
 * Fabric v6 does not update x1..y2 when a line is moved (only left/top
 * change), but setting x1..y2 later — as the endpoint controls and the
 * grid-snap scaling handler do — re-derives left/top from those stale raw
 * coordinates. Result: after moving a line, the first endpoint drag makes
 * the OPPOSITE endpoint jump back to its pre-move position (latent bug,
 * became visible through linked gates, #611).
 *
 * Calling this before a transform starts keeps raw and world coordinates
 * consistent; the visual position is unchanged.
 */
export const normalizeLineRawCoords = (line) => {
  if (!line || line.type !== 'line' || typeof line.calcLinePoints !== 'function') return;
  const p = line.calcLinePoints();
  const a = transformLocalPoint(line, { x: p.x1, y: p.y1 });
  const b = transformLocalPoint(line, { x: p.x2, y: p.y2 });
  line.set({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
  line.setCoords();
};

// ---------------------------------------------------------------------------
// Canvas-level helpers (sync)
// ---------------------------------------------------------------------------

/**
 * Linkable parent structures on the canvas (excluding grid/temp objects).
 * Pass allowedTypes to restrict candidates to a specific opening kind — a
 * window (#690) only snaps onto buildings/greenhouses, not fences or walls.
 * Omitting it keeps the historical behaviour (all opening-hosting types).
 */
export const findLinkableParents = (canvas, allowedTypes = LINKABLE_TYPE_IDS) => {
  if (!canvas) return [];
  return canvas
    .getObjects()
    .filter(
      (obj) =>
        isLinkableParent(obj, allowedTypes) &&
        !obj.data?.isTemporary &&
        !obj.data?.isGridLine
    );
};

/**
 * Resolve a parent structure by its stable objectId.
 */
export const resolveParentById = (canvas, parentId) => {
  if (!canvas || !parentId) return null;
  return (
    canvas
      .getObjects()
      .find((obj) => isLinkableParent(obj) && obj.data?.objectId === parentId) ||
    null
  );
};

/**
 * All openings (gates AND windows, #690) linked to the given parent. Windows
 * follow their parent exactly like gates, so the whole sync/reproject/detach
 * machinery below covers both kinds through this one predicate.
 */
export const findLinkedGates = (canvas, parent) => {
  const parentId = parent?.data?.objectId;
  if (!canvas || !parentId) return [];
  return canvas
    .getObjects()
    .filter((obj) => isOpening(obj) && obj.data?.link?.parentId === parentId);
};

/**
 * Re-derive position/angle (and inherited height) of all gates linked to a
 * parent from the parent's CURRENT geometry. Called on parent move/rotate/
 * scale — live during drag and from TransformObjectCommand execute/undo, so
 * undo/redo stays consistent without extra bookkeeping.
 *
 * @returns {number} number of gates that were repositioned
 */
export const syncLinkedGates = (canvas, parent) => {
  const gates = findLinkedGates(canvas, parent);
  let synced = 0;

  gates.forEach((gate) => {
    const pos = getLinkWorldPosition(parent, gate.data.link);
    if (!pos) return;

    // Edge shorter than the gate → the gate cannot fit; freeze it in place.
    // detachGatesFromShortEdges() cuts the link at drag end.
    const gateWidthPx = (gate.width || 0) * (gate.scaleX || 1);
    if (pos.edgeLength < gateWidthPx) return;

    // Clamp the stored t to the current edge length so the gate never
    // overhangs an end when the structure got shorter (persist if changed)
    const t = clampGateT(gate.data.link.t ?? 0.5, pos.edgeLength, gateWidthPx);
    if (t !== gate.data.link.t) {
      gate.data = { ...gate.data, link: { ...gate.data.link, t } };
    }

    gate.set({ angle: pos.angleDeg });
    setObjectCenterAt(gate, pointOnEdgeAt(pos.edge, t));
    applyInheritedHeight(gate, parent);
    gate.setCoords();
    synced += 1;
  });

  return synced;
};

/**
 * Re-project linked gates onto the parent's CURRENT geometry, updating the
 * stored t so each gate stays at (or as close as possible to) its world
 * position. Used when the parent's geometry itself changes (line endpoint
 * drag, rect scaling) — a purely proportional t would make the gate slide
 * along the structure instead of staying put.
 *
 * @returns {number} number of gates that were re-projected
 */
export const reprojectLinkedGates = (canvas, parent) => {
  const gates = findLinkedGates(canvas, parent);
  let updated = 0;

  gates.forEach((gate) => {
    const center = typeof gate.getCenterPoint === 'function'
      ? gate.getCenterPoint()
      : { x: gate.left, y: gate.top };
    const snap = findNearestEdgePoint({ x: center.x, y: center.y }, [parent], Infinity);
    if (!snap) return;

    // Edge shorter than the gate → freeze (see syncLinkedGates)
    const gateWidthPx = (gate.width || 0) * (gate.scaleX || 1);
    if (snap.edgeLength < gateWidthPx) return;

    const t = clampGateT(snap.t, snap.edgeLength, gateWidthPx);
    gate.data = {
      ...gate.data,
      link: { ...gate.data.link, edgeIndex: snap.edgeIndex, t },
    };
    gate.set({ angle: snap.angleDeg });
    setObjectCenterAt(gate, pointOnEdgeAt(snap.edge, t));
    applyInheritedHeight(gate, parent);
    gate.setCoords();
    updated += 1;
  });

  return updated;
};

/**
 * Cut the link of gates whose edge has become shorter than the gate itself
 * (structure was shrunk below the gate width). Called at drag end — the gate
 * stays at its frozen position and becomes standalone, mirroring the
 * drag-away detach semantics. Re-attach by dragging the gate onto a
 * structure that fits.
 *
 * @returns {number} number of gates that were detached
 */
export const detachGatesFromShortEdges = (canvas, parent) => {
  const gates = findLinkedGates(canvas, parent);
  let detached = 0;

  gates.forEach((gate) => {
    const pos = getLinkWorldPosition(parent, gate.data.link);
    if (!pos) return;
    const gateWidthPx = (gate.width || 0) * (gate.scaleX || 1);
    if (pos.edgeLength >= gateWidthPx) return;

    const { link: _removed, ...rest } = gate.data;
    gate.data = rest;
    detached += 1;
  });

  return detached;
};

/**
 * Resync every linked gate on the canvas (e.g. after canvas load).
 */
export const syncAllLinkedGates = (canvas) => {
  if (!canvas) return 0;
  let synced = 0;
  findLinkableParents(canvas).forEach((parent) => {
    if (parent.data?.objectId) {
      synced += syncLinkedGates(canvas, parent);
    }
  });
  return synced;
};
